import { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../redux/store/store';
import {
  useGetUserQuery,
  useGetAccountDeletionEligibilityQuery,
  useRequestAccountDeletionMutation,
  useCancelAccountDeletionMutation,
  useTransferOrganizationOwnershipMutation,
} from '../redux/api';
import { Modal } from './Modal';
import { IconTrash, IconRotateCcw } from './icons';
import { logout as clearAuthSession } from '../redux/slice/authSlice';
import { logout as clearLayoutSession } from '../redux/slice/layoutSlice';
import { currentUserId } from '../utils/auth';

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

interface AccountDeletionPanelProps {
  /** `panel` renders inside a page section (Profile); `bare` suits the
   *  narrow login-style cards (e.g. the pending-approval screen). */
  variant?: 'panel' | 'bare';
}

// Self-service "delete my account" (Apple App Store 5.1.1(v) / Google Play
// account-deletion policy). Shared so every screen a signed-in user can
// actually reach offers the same flow — notably the pending-approval screen,
// where someone whose subscription was never activated would otherwise be
// stuck with no way to delete their account at all.
export const AccountDeletionPanel = ({ variant = 'panel' }: AccountDeletionPanelProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const myId = currentUserId();
  const { data: me } = useGetUserQuery(myId ?? '', { skip: !myId });

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transferPicks, setTransferPicks] = useState<Record<string, string>>({});

  const {
    data: eligibility,
    isFetching: loadingEligibility,
    refetch: refetchEligibility,
  } = useGetAccountDeletionEligibilityQuery(undefined, { skip: !open });
  const [requestAccountDeletion, { isLoading: requesting }] = useRequestAccountDeletionMutation();
  const [cancelAccountDeletion, { isLoading: restoring }] = useCancelAccountDeletionMutation();
  const [transferOwnership, { isLoading: transferring }] =
    useTransferOrganizationOwnershipMutation();

  const openDelete = () => {
    setError(null);
    setTransferPicks({});
    setOpen(true);
  };

  const handleTransferAndContinue = async () => {
    if (!eligibility) return;
    setError(null);
    const missing = eligibility.blockers.find((b) => !transferPicks[b.organizationId]);
    if (missing) {
      setError(`Choose a new owner for "${missing.organizationName}".`);
      return;
    }
    try {
      await Promise.all(
        eligibility.blockers.map((b) =>
          transferOwnership({
            organizationId: b.organizationId,
            newOwnerUserId: transferPicks[b.organizationId],
          }).unwrap(),
        ),
      );
      await refetchEligibility();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to transfer ownership.');
    }
  };

  const handleConfirmDelete = async () => {
    setError(null);
    try {
      await requestAccountDeletion().unwrap();
      dispatch(clearAuthSession());
      dispatch(clearLayoutSession());
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to delete account.');
    }
  };

  const handleRestore = async () => {
    setError(null);
    try {
      await cancelAccountDeletion().unwrap();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to restore account.');
    }
  };

  const blocked = !!eligibility && eligibility.blockers.length > 0;

  const body = me?.pendingDeletion ? (
    <>
      <p className={variant === 'panel' ? 'profile-empty' : 'login__hint'}>
        Your account is scheduled for deletion on{' '}
        <strong>{formatDate(me.pendingDeletion.scheduledFor)}</strong>. You can restore it any time
        before then.
      </p>
      {error ? <div className="form-error">{error}</div> : null}
      <button
        type="button"
        className={`btn btn--primary${variant === 'bare' ? ' login__submit' : ''}`}
        onClick={handleRestore}
        disabled={restoring}
      >
        <IconRotateCcw /> {restoring ? 'Restoring…' : 'Restore my account'}
      </button>
    </>
  ) : (
    <>
      <p className={variant === 'panel' ? 'profile-empty' : 'login__hint'}>
        Deleting your account soft-deletes it for a grace period (30 days by default, configurable
        by your platform administrator) — you can restore it any time before then by signing back
        in.
      </p>
      <button
        type="button"
        className={`btn btn--danger${variant === 'bare' ? ' login__submit' : ''}`}
        onClick={openDelete}
      >
        <IconTrash /> Delete my account
      </button>
    </>
  );

  return (
    <>
      {variant === 'panel' ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h2>Danger zone</h2>
            </div>
          </div>
          {body}
        </section>
      ) : (
        body
      )}

      <Modal
        open={open}
        title="Delete my account"
        onClose={() => setOpen(false)}
        footer={
          blocked ? (
            <>
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleTransferAndContinue}
                disabled={transferring}
              >
                {transferring ? 'Transferring…' : 'Transfer & continue'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={handleConfirmDelete}
                disabled={requesting || loadingEligibility}
              >
                {requesting ? 'Deleting…' : 'Yes, delete my account'}
              </button>
            </>
          )
        }
      >
        {error ? <div className="form-error">{error}</div> : null}
        {loadingEligibility ? (
          <p className="profile-empty">Checking…</p>
        ) : blocked ? (
          <>
            <p>
              You're the only owner of the following organization(s). Choose a new owner for each
              before your account can be deleted:
            </p>
            <div className="form-grid">
              {eligibility!.blockers.map((blocker) => (
                <label className="field field--full" key={blocker.organizationId}>
                  {blocker.organizationName} — new owner
                  <select
                    value={transferPicks[blocker.organizationId] ?? ''}
                    onChange={(e) =>
                      setTransferPicks((prev) => ({
                        ...prev,
                        [blocker.organizationId]: e.target.value,
                      }))
                    }
                  >
                    <option value="">— Select —</option>
                    {blocker.otherMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </>
        ) : (
          <>
            <p>
              This will schedule your account for deletion. You'll be signed out immediately, but
              can restore your account any time within the grace period by signing back in.
            </p>
            {eligibility && eligibility.soloOrganizations.length > 0 ? (
              <p>
                Since you're the only member of{' '}
                {eligibility.soloOrganizations.map((o) => `"${o.organizationName}"`).join(', ')},
                that organization will be deleted along with your account.
              </p>
            ) : null}
          </>
        )}
      </Modal>
    </>
  );
};

export default AccountDeletionPanel;
